import { useState, useEffect } from 'react';
import { Loader2, Save } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { useToast } from '@/hooks/use-toast';
import AdminLayout from '@/components/admin/AdminLayout';
import { supabase } from '@/integrations/supabase/client';

const MODELS = [
  'google/gemini-3.6-flash',
  'google/gemini-3.5-flash',
  'google/gemini-2.5-pro',
  'google/gemini-2.5-flash',
  'openai/gpt-5.4-mini',
];

const AdminAutoBlogSettings = () => {
  const { toast } = useToast();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [authors, setAuthors] = useState<{ id: string; name: string }[]>([]);
  const [form, setForm] = useState({
    brand_name: '', domain_name: '', sitemap_url: '', default_author: '',
    default_author_id: '', default_category: '', model: '', prompt_template: '',
  });

  useEffect(() => {
    (async () => {
      const [s, a] = await Promise.all([
        supabase.from('blog_settings').select('*').eq('id', 1).maybeSingle(),
        supabase.from('blog_authors').select('id, name').order('name'),
      ]);
      if (s.data) {
        setForm({
          brand_name: s.data.brand_name, domain_name: s.data.domain_name,
          sitemap_url: s.data.sitemap_url, default_author: s.data.default_author,
          default_author_id: s.data.default_author_id || '',
          default_category: s.data.default_category, model: s.data.model,
          prompt_template: s.data.prompt_template,
        });
      }
      setAuthors(a.data || []);
      setLoading(false);
    })();
  }, []);

  const save = async () => {
    setSaving(true);
    const { error } = await supabase.from('blog_settings').update({
      ...form, default_author_id: form.default_author_id || null,
    }).eq('id', 1);
    setSaving(false);
    if (error) toast({ title: 'Error', description: error.message, variant: 'destructive' });
    else toast({ title: 'Settings saved' });
  };

  if (loading) {
    return <AdminLayout><div className="flex justify-center py-20"><Loader2 className="h-6 w-6 animate-spin text-primary" /></div></AdminLayout>;
  }

  return (
    <AdminLayout>
      <div className="space-y-6 max-w-3xl">
        <div>
          <h1 className="text-3xl font-bold text-foreground">Auto-Blog Settings</h1>
          <p className="text-muted-foreground mt-1">Defaults used by every generated article</p>
        </div>

        <Card>
          <CardHeader><CardTitle>Brand &amp; site</CardTitle></CardHeader>
          <CardContent className="space-y-4">
            <div className="grid gap-4 sm:grid-cols-2">
              <div className="space-y-2"><Label>Brand name</Label><Input value={form.brand_name} onChange={e => setForm({ ...form, brand_name: e.target.value })} /></div>
              <div className="space-y-2"><Label>Domain</Label><Input value={form.domain_name} onChange={e => setForm({ ...form, domain_name: e.target.value })} /></div>
            </div>
            <div className="space-y-2"><Label>Sitemap URL</Label><Input value={form.sitemap_url} onChange={e => setForm({ ...form, sitemap_url: e.target.value })} /></div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader><CardTitle>Defaults</CardTitle></CardHeader>
          <CardContent className="space-y-4">
            <div className="grid gap-4 sm:grid-cols-2">
              <div className="space-y-2">
                <Label>Default author</Label>
                <Select value={form.default_author_id} onValueChange={v => {
                  const name = authors.find(a => a.id === v)?.name || form.default_author;
                  setForm({ ...form, default_author_id: v, default_author: name });
                }}>
                  <SelectTrigger><SelectValue placeholder="Select author" /></SelectTrigger>
                  <SelectContent>{authors.map(a => <SelectItem key={a.id} value={a.id}>{a.name}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div className="space-y-2"><Label>Default category label</Label><Input value={form.default_category} onChange={e => setForm({ ...form, default_category: e.target.value })} /></div>
            </div>
            <div className="space-y-2">
              <Label>AI model</Label>
              <Select value={form.model} onValueChange={v => setForm({ ...form, model: v })}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>{MODELS.map(m => <SelectItem key={m} value={m}>{m}</SelectItem>)}</SelectContent>
              </Select>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>Prompt template</CardTitle>
            <CardDescription>Tokens: {'{{keyword}}'}, {'{{lsi_keywords}}'}, {'{{brand_name}}'}, {'{{domain_name}}'}, {'{{sitemap_url}}'}</CardDescription>
          </CardHeader>
          <CardContent>
            <Textarea rows={20} className="font-mono text-xs" value={form.prompt_template} onChange={e => setForm({ ...form, prompt_template: e.target.value })} />
          </CardContent>
        </Card>

        <Button onClick={save} disabled={saving}>
          {saving ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Save className="mr-2 h-4 w-4" />}Save settings
        </Button>
      </div>
    </AdminLayout>
  );
};

export default AdminAutoBlogSettings;
